import { getLinkedinTier } from "@/utils/getLinkedinTier";

import { SenderRecipientOption } from "../components/SenderRecipientDropdown";

import { LinkedinSenderStates } from "./filterLinkedinSenders.util";
import { isMailboxHealthy } from "./resolveMyDefaultSender.util";

import type { User } from "@/pages/Public/pages/Login/schema/login";

interface BuildSenderOptionsParams {
	users: User[];
	connectionCheckDone: boolean;
	connectedUserIds: Set<number>;
	/** Whose invitation is pending, whose state is unknown (PREC-4216); absent reads as neither. */
	senderStates?: Pick<LinkedinSenderStates, "pendingInvitations" | "unknownUserIds">;
	prospectHasEmail: boolean;
	prospectHasLinkedinUrl: boolean;
	currentUserId?: number;
}

const senderDisplayName = (user: User, currentUserId?: number): string =>
	user.id === currentUserId ? "Me" : `${user.firstName} ${user.lastName}`;

const NO_LINKEDIN_URL_REASON = "Prospect has no LinkedIn profile URL.";
const RECONNECT_MAILBOX_REASON_KEY = "inbox.identity.reconnectMailbox";
const STATE_UNKNOWN_REASON_KEY = "inbox.identity.linkedinStateUnknown";

/** A LinkedIn sender's label suffix and channel badge, by what a message from them would do. */
const linkedinLabel = (
	userId: number,
	connectedUserIds: Set<number>,
	senderStates: BuildSenderOptionsParams["senderStates"]
): { suffix: string; badgeKey: string } => {
	if (connectedUserIds.has(userId)) {
		return { suffix: "LinkedIn", badgeKey: "inbox.identity.linkedin" };
	}

	if (senderStates?.pendingInvitations.has(userId)) {
		return {
			suffix: "LinkedIn — invitation pending",
			badgeKey: "inbox.identity.linkedinInvitationPending",
		};
	}

	if (senderStates?.unknownUserIds.has(userId)) {
		return { suffix: "LinkedIn — state unknown", badgeKey: "inbox.identity.linkedinUnknown" };
	}

	return { suffix: "LinkedIn — will send invite", badgeKey: "inbox.identity.linkedinInvite" };
};

/** Why a LinkedIn sender cannot be picked, if they cannot: no profile URL, or an unknown state. */
const linkedinDisabled = (
	userId: number,
	prospectHasLinkedinUrl: boolean,
	senderStates: BuildSenderOptionsParams["senderStates"]
): Partial<SenderRecipientOption> => {
	if (!prospectHasLinkedinUrl) {
		return { disabled: true, disabledReason: NO_LINKEDIN_URL_REASON };
	}

	// Fail closed (PREC-4216): neither a message that may fail nor an invitation that may be a
	// second one goes out while we do not know the sender's state.
	return senderStates?.unknownUserIds.has(userId)
		? { disabled: true, disabledReasonKey: STATE_UNKNOWN_REASON_KEY }
		: {};
};

/**
 * Builds the list of sender options for the chat sender dropdown.
 *
 * LinkedIn senders are always shown for users with a Unipile session once the
 * connection check has completed:
 * - Connected to the prospect, or a chat exists → "(LinkedIn)" label, enabled.
 * - The sender's own invitation is pending (PREC-4216) → "(LinkedIn — invitation pending)",
 *   enabled: the message waits for the acceptance, it is never an invite note.
 * - The check failed (PREC-4216) → "(LinkedIn — state unknown)", disabled with the reason.
 * - Otherwise → "(LinkedIn — will send invite)" label, enabled. Selecting
 *   such a sender auto-flags the first draft as an invite note.
 * - When the prospect has no LinkedIn URL, LinkedIn senders are still listed
 *   but disabled, with a tooltip explaining why.
 *
 * Email senders are only shown when the prospect has an email address. A mailbox that is not
 * connected (see `isMailboxHealthy`) is listed disabled with the reconnect hint (PREC-4312).
 *
 * The current user (matched by `currentUserId`) is rendered as "Me" instead of their full name.
 */
export const buildSenderOptions = ({
	users,
	connectionCheckDone,
	connectedUserIds,
	senderStates,
	prospectHasEmail,
	prospectHasLinkedinUrl,
	currentUserId,
}: BuildSenderOptionsParams): SenderRecipientOption[] => {
	return users.flatMap((u) => {
		const options: SenderRecipientOption[] = [];
		const displayName = senderDisplayName(u, currentUserId);

		if (u.unipile?.isLoggedIn && connectionCheckDone) {
			const { suffix, badgeKey } = linkedinLabel(u.id, connectedUserIds, senderStates);

			options.push({
				id: `li-${u.id}`,
				label: `${displayName} (${suffix})`,
				type: "linkedin",
				userId: u.id,
				displayName,
				linkedinTier: getLinkedinTier(u),
				channelBadgeKey: badgeKey,
				...linkedinDisabled(u.id, prospectHasLinkedinUrl, senderStates),
			});
		}

		// Only show email senders when the prospect has an email address.
		// A user can have several connected mailboxes ("identities"); each gets
		// its own option, tagged Primary/Secondary from the mailbox's `type`.
		if (prospectHasEmail) {
			u.emails?.forEach((email) => {
				options.push({
					id: `email-${u.id}-${email.id}`,
					label: `${displayName} (${email.email})`,
					type: "email",
					userId: u.id,
					emailId: email.id ?? undefined,
					emailAddress: email.email,
					displayName,
					tag: email.type === "secondary" ? "secondary" : "primary",
					sublabel: email.email,
					...(isMailboxHealthy(email)
						? {}
						: { disabled: true, disabledReasonKey: RECONNECT_MAILBOX_REASON_KEY }),
				});
			});
		}

		return options;
	});
};
